import qs from 'qs';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { BrandStoryLanding } from 'brand/components/BrandStoryLanding';
import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { cmsRequest } from 'server/services/cms-service/cmsRequest';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { getThemeBackgroundColorFromSpaceArticle } from 'shared/utils/getThemeBackgroundColorFromSpaceArticle';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { ISpaceArticleSectionFeed } from 'shared/types/SpaceArticleSection';
import { navigationThemes } from 'styles/theme/navigation';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { setToggleCurrentTheme } from 'store/slice/pageLayoutSlice';
import { startCase } from 'shared/utils/startCase';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { getCollectionsGrpc } from 'server/grpc/collections/collections.grpc';
import { requestService } from 'shared/utils/services/requestService';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import {
  CONSUMER_SEARCH_BRANDS_REDIRECTS,
  CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS,
  CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS,
} from 'brand/enum/consumerSearchRedirects';
import { normalizeValue } from 'shared/utils/normalizeValue';

function replaceUndefinedWithNull(obj) {
  if (!obj) return null;
  if (Array.isArray(obj)) {
    // eslint-disable-next-line unicorn/no-array-callback-reference
    return obj.map(replaceUndefinedWithNull);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, replaceUndefinedWithNull(value)]),
    );
  }
  return obj === undefined ? null : obj;
}
interface IBrandProps extends IProductLandingPage {
  article?: ISpaceArticleSectionFeed;
  backgroundColor?: string | null;
  collectionDisplayName?: string;
  isInApp?: boolean;
  metaImage?: string | null;
  showCollection?: string;
  isCrawler: boolean;
  consumerSearchCollection?: any;
  consumerSearchFilters?: any;
  consumerSearchInitialData?: string;
}

const Brand = ({
  article,
  backgroundColor,
  breadcrumbs,
  browseKey,
  browseValue,
  collectionDisplayName,
  constructorResponse,
  displayName,
  isInApp = false,
  metaImage,
  globalPricingId,
  showCollection,
  isCrawler,
  consumerSearchCollection = null,
  consumerSearchFilters,
  consumerSearchInitialData = '{}',
}: IBrandProps) => {
  const storeDispatch = useStoreDispatch();

  const [consumerCollection, setConsumerCollection] = useState<any>(consumerSearchCollection);
  const {
    name = displayName,
    description = '',
    headerAsset = { url: '' },
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  const isMounted = useIsMounted();
  const router = useRouter();

  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const [consumerAtricles, setConsumerArticles] = useState<any>({
    article: article || null,
    backgroundColor: backgroundColor || null,
  });

  useEffect(() => {
    const getCSArticle = async () => {
      try {
        const res2 = await requestService({
          url: `/web-api/consumer-search/get-collection-details?slug=${router?.query?.slug as string}`,
          method: 'get',
        });

        setConsumerCollection(res2?.data?.collection || {});
        if (res2?.data?.collection?.publishedCmsArticleSlug) {
          const { publishedCmsArticleSlug } = res2?.data?.collection || {};

          try {
            const data2 = await requestService({
              url: `/web-api/v1/cms/cms_articles/${publishedCmsArticleSlug}`,
              method: 'get',
            });

            if (data2) {
              backgroundColor =
                data2?.sections?.length > 0
                  ? getThemeBackgroundColorFromSpaceArticle(data2?.sections?.[0])
                  : null;
              setConsumerArticles({
                article: data2,
                backgroundColor,
              });
            }
          } catch (error) {
            bugsnag(error, { context: 'Failed to fetch CMS article for Manifesto' });

            setConsumerArticles({
              article: null,
              backgroundColor: null,
            });
          }
        }
      } catch (error) {
        bugsnag(error, { context: 'Failed to fetch CMS Space for Manifesto' });
        setConsumerCollection({});
        setConsumerArticles({
          article: null,
          backgroundColor: null,
        });
      }
    };

    if (isNewSearchPageEnabled) {
      void getCSArticle();
    }
  }, [isNewSearchPageEnabled, router.query?.slug]);

  const enableBrandStory =
    useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_BRAND_STORY) && article?.sections?.length > 0;

  useEffect(() => {
    if (!enableBrandStory && isMounted()) {
      storeDispatch(setToggleCurrentTheme(navigationThemes.light));
    }
  }, [enableBrandStory]);

  if (
    isNewSearchPageEnabled &&
    (!consumerCollection || !consumerAtricles) &&
    typeof window !== 'undefined'
  ) {
    return null; // or a loading spinner, or any placeholder you prefer
  }

  return enableBrandStory ? (
    <BrandStoryLanding
      article={isNewSearchPageEnabled ? consumerAtricles.article : article}
      backgroundColor={isNewSearchPageEnabled ? consumerAtricles.backgroundColor : backgroundColor}
      brandName={displayName}
      browseKey={browseKey}
      browseValue={browseValue}
      displayName={consumerCollection?.title || name}
      isInApp={isInApp}
      collectionDisplayName={consumerCollection?.title || collectionDisplayName}
      constructorResponse={constructorResponse}
      description={consumerCollection?.description || description}
      globalPricingId={globalPricingId}
      metadataDescription={consumerCollection?.metadataDescription || metadataDescription}
      metaTitle={`${consumerCollection?.title || titleTag || displayName} `}
      showCollection={(router?.query?.showCollection || showCollection) as any}
      isCrawler={isCrawler}
      consumerSearchFilters={consumerSearchFilters || { brands: router?.query?.slug || '' }}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
    />
  ) : (
    <PLPLandingPageComponent
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters || { brands: router?.query?.slug || '' }}
      description={consumerCollection?.description || description}
      displayName={consumerCollection?.title || name}
      globalPricingId={globalPricingId}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
      isCrawler={isCrawler}
      metadataDescription={consumerCollection?.metadataDescription || metadataDescription}
      metaImage={metaImage || headerAsset?.url}
      metaTitle={`${consumerCollection?.title || titleTag || displayName} `}
      isBrandsPath
    />
  );
};

export const getServerSideProps = async ({ query, req, res, locale }) => {
  const { slug }: { slug: string } = query;
  const q = qs.parse(query);
  const { showCollection = 'false', app } = q;
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.BRAND}/${CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS[slug] || slug}`,
  });

  const isCrawler = isSearchCrawler(req?.headers);

  // Check if collection for this slug exists, if not browse for brand
  const brandName: string = startCase(slug, {
    ignoreSpecialDisplay: true,
  }).toLowerCase();

  const brandDisplayName: string = startCase(slug);
  const normalizedBrandName = normalizeValue(slug);

  // BUY3-1381: need to redirect special character brand names to normalized value
  // as Google is still crawling V1 GOAT
  if (normalizedBrandName !== brandName) {
    return {
      redirect: {
        destination: getRedirectionPath(`/brand/${normalizedBrandName}`, locale),
        permanent: true,
      },
    };
  }

  if (CONSUMER_SEARCH_BRANDS_REDIRECTS[slug]) {
    return {
      redirect: {
        destination: getRedirectionPath(`/brand/${CONSUMER_SEARCH_BRANDS_REDIRECTS[slug]}`, locale),
        permanent: false,
      },
    };
  }
  // If consumer search is enabled, we need to fetch the consumer data details
  const consumerSearchInitialData = await getConsumerSearchServerSideData({
    filters: {
      ...q,
      pageFilters: {
        brands: CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS[slug.toLowerCase()] || slug,
      },
    },
    pageNumber: req.query?.pageNumber || 1,
  });

  if (!consumerSearchInitialData?.items?.hasData) {
    return { notFound: true };
  }

  let backgroundColor = '';
  let article = null;
  const grpcClient = getCollectionsGrpc();
  let details: any = null;
  try {
    const res2 = await grpcClient.getCollectionsDetails({
      slug: query.slug,
      salesChannel: 1,
      id: null,
    });

    details = res2?.data?.collection;

    if (res2?.data?.collection?.publishedCmsArticleSlug) {
      const { publishedCmsArticleSlug } = res2?.data?.collection || {};

      try {
        const data = cmsRequest(req).get(`api/v1/cms/cms_articles/${publishedCmsArticleSlug}`);
        article = await data.json();

        backgroundColor =
          article?.sections?.length > 0
            ? getThemeBackgroundColorFromSpaceArticle(article?.sections?.[0])
            : null;
      } catch (error) {
        bugsnag(error, { context: 'Failed to fetch CMS article for Manifesto' });
      }
    }
  } catch (error) {
    bugsnag(error, { context: 'Failed to fetch CMS Space for Manifesto' });
  }

  return {
    props: {
      ...(await setServerSideTranslations({ locale })),
      breadcrumbs,
      browseKey: 'brand',
      browseValue: brandName,
      constructorResponse: {},
      consumerSearchInitialData: JSON.stringify({}),
      consumerSearchFilters: {
        brands: CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS[slug.toLowerCase()] || slug,
      },
      displayName: brandDisplayName,
      globalPricingId,
      showCollection,
      slug,
      isCrawler,
      article,
      backgroundColor,
      consumerSearchCollection: replaceUndefinedWithNull(details),
      isInApp: app === 'y',
      metaImage: details?.headerAsset?.type === 'picture' ? details?.headerAsset?.url : null,
    },
  };
};

export default Brand;
